import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { H1 } from 'foundations-components/transitional/components/H1';
import { Section } from 'foundations-components/transitional/components/Section';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tab, Tabs } from 'foundations-components/transitional/components/Tabs';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollEventListener } from 'marketplace-ui-common/hooks/useScrollEventListener';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { Fragment, useCallback, useMemo } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { getLocationFromQuery } from '../../common/utils/assetQuery';
import { UnifiedBreadcrumbs } from '../../components/breadcrumbs/UnifiedBreadcrumbs';
import { ModuleQuery } from '../../modules/records/ModuleQuery';
import { ThemeQuery } from '../../themes/records/ThemeQuery';
import { getAssetEventSelectorId } from '../../utils/hsAnalytics';
import AssetDemoButton from './AssetDemoButton';
import AssetDownloadButton from './AssetDownloadButton';
import AssetSetupGuideButton from './AssetSetupGuideButton';
import AssetShareButton from './AssetShareButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TABS_HEIGHT = 49;
const NoopLink = ({
  children
}) => children;
export default function AssetDetailsHeader({
  assetDetails,
  assetScopes,
  exampleUrl,
  isEmbedded,
  isPreview,
  name,
  offeringType,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  reviewSummary
}) {
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const {
    setupUrl
  } = assetDetails;
  const {
    height: windowHeight
  } = useWindowDimensionsContext();
  const scrollOffset = TABS_HEIGHT + windowHeight / 2;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(['overview', 'features', 'contents', 'support', 'reviews'], scrollOffset);
  useScrollEventListener(calculateActiveTab);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'header'
    }));
  }, [onTrackInteraction]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    trackInteraction('click breadcrumb link', {
      value
    });
  }, [trackInteraction]);
  const trackTabClick = useCallback(value => {
    trackInteraction('click tab navigation', {
      value
    });
  }, [trackInteraction]);
  const providerQuery = useMemo(() => {
    return offeringType === 'MODULE' ? new ModuleQuery({
      providers: [providerName]
    }) : new ThemeQuery({
      providers: [providerName]
    });
  }, [offeringType, providerName]);
  const location = useLocation();
  const providerAssetLocation = useMemo(() => {
    return getLocationFromQuery(providerQuery, location);
  }, [location, providerQuery]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isEmbedded && /*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(UnifiedBreadcrumbs, {
        linkUse: undefined,
        path: offeringType === 'MODULE' ? 'module-details' : 'theme-details',
        currentItemName: name,
        isPreview: isPreview,
        onBreadcrumbClick: trackBreadcrumbLinkClick
      })
    }), /*#__PURE__*/_jsxs(Section, {
      paddingBlockStart: isEmbedded ? '1000' : undefined,
      marginBlockEnd: isMobileView ? '0' : undefined,
      children: [/*#__PURE__*/_jsx(H1, {
        "aria-level": 1,
        children: name
      }), /*#__PURE__*/_jsxs(TFlex, {
        align: "baseline",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(Badge, {
          use: "koala",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-assets-ui.details.offeringType.${offeringType}`
          })
        }), /*#__PURE__*/_jsx(View, {
          as: "span",
          display: "inline-block",
          marginInline: "300",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.details.header.providerName_jsx",
            options: {
              providerName,
              linkProps: {
                'data-hs-analytics-id': getAssetEventSelectorId('HEADER-PROVIDER-WEBSITE', offeringType),
                to: providerAssetLocation,
                onClick: () => trackInteraction('click provider link')
              }
            },
            elements: {
              Link: isEmbedded ? NoopLink : RouterLink
            }
          })
        }), !!(reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.reviewCount) && /*#__PURE__*/_jsx(Rating, {
          iconSize: "300",
          label: /*#__PURE__*/_jsx(Button, {
            onClick: () => {
              scrollToTabSection('reviews');
              trackInteraction('click review count');
            },
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.details.header.reviewCount",
              options: {
                count: reviewSummary.reviewCount
              }
            })
          }),
          rating: reviewSummary.overallRating
        })]
      })]
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: "md",
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(View, {
          as: "div",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(Section, {
            paddingBlockStart: "400",
            children: /*#__PURE__*/_jsxs(TFlex, {
              children: [/*#__PURE__*/_jsx("div", {
                style: {
                  flexBasis: '50%'
                },
                children: /*#__PURE__*/_jsx(AssetDownloadButton, {
                  marginInlineEnd: isMobileView ? '300' : '0',
                  assetDetails: assetDetails,
                  assetScopes: assetScopes,
                  offeringType: offeringType,
                  section: "header"
                })
              }), /*#__PURE__*/_jsx("div", {
                style: {
                  flexBasis: '50%'
                },
                children: /*#__PURE__*/_jsx(AssetDemoButton, {
                  marginInlineStart: "0",
                  exampleUrl: exampleUrl,
                  modalPrimaryButton: /*#__PURE__*/_jsx(AssetDownloadButton, {
                    assetDetails: assetDetails,
                    assetScopes: assetScopes,
                    offeringType: offeringType
                  }),
                  name: name,
                  onTrackInteraction: onTrackInteraction,
                  onTrackPageview: onTrackPageview,
                  providerName: providerName,
                  section: "header"
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          as: "div",
          textAlign: "center",
          children: /*#__PURE__*/_jsxs(Section, {
            paddingBlock: "0",
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              blockSize: "auto",
              marginInlineEnd: "300",
              children: /*#__PURE__*/_jsx(AssetSetupGuideButton, {
                offeringType: offeringType,
                setupUrl: setupUrl
              })
            }), /*#__PURE__*/_jsx(AssetShareButton, {
              section: "header",
              style: {
                width: 'auto'
              },
              use: "link"
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(TFlex, {
            align: "center",
            justify: "between",
            children: /*#__PURE__*/_jsx(MediaQueryView, {
              min: "md",
              inlineSize: "100%",
              children: /*#__PURE__*/_jsx(Grid, {
                children: /*#__PURE__*/_jsx(GridItem, {
                  size: {
                    xs: 12,
                    md: 8
                  },
                  children: /*#__PURE__*/_jsxs(Tabs, {
                    bordered: false,
                    defaultSelected: "overview",
                    onSelectedChange: event => {
                      scrollToTabSection(event.target.value);
                    },
                    selected: activeTab,
                    children: [/*#__PURE__*/_jsx(Tab, {
                      onClick: () => trackTabClick('overview'),
                      tabId: "overview",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.overview"
                      })
                    }), /*#__PURE__*/_jsx(Tab, {
                      onClick: () => trackTabClick('features'),
                      tabId: "features",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.features"
                      })
                    }), offeringType === 'THEME' && /*#__PURE__*/_jsx(Tab, {
                      onClick: () => trackTabClick('contents'),
                      tabId: "contents",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.contents"
                      })
                    }), /*#__PURE__*/_jsx(Tab, {
                      onClick: () => trackTabClick('support'),
                      tabId: "support",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.support"
                      })
                    }), /*#__PURE__*/_jsx(Tab, {
                      onClick: () => trackTabClick('reviews'),
                      tabId: "reviews",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.reviews"
                      })
                    })]
                  })
                })
              })
            })
          })
        })
      })
    })]
  });
}